/* eslint-disable camelcase */

import React, { useCallback, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  HvAtmosphereColorKeys,
  HvButton,
  HvCard,
  HvGrid,
  HvMultiButton,
  HvSemanticColorKeys,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import { List, Map } from "@hitachivantara/uikit-react-icons";
import { MapContext } from "lib/context/MapContext";
import { OverflowTooltip } from "components/common";

import { KPI, KpiNames, useKpis } from "lib/api";
import useStyles from "./styles";

interface KPIwithColor extends KPI {
  color: "sema0" | HvSemanticColorKeys | HvAtmosphereColorKeys;
}

const getColor = (
  name: KpiNames,
  value: number
): "sema0" | HvSemanticColorKeys | HvAtmosphereColorKeys => {
  switch (name) {
    case "KPI_OPEN_INCIDENT": {
      return value > 0 ? "sema3" : "atmo5";
    }
    case "KPI_TODAY_INCIDENT": {
      return value > 0 ? "sema3" : "atmo5";
    }
    case "KPI_UNASSIGNED_INCIDENT": {
      return value > 0 ? "sema3" : "atmo5";
    }
    case "KPI_OVERDUE_INCIDENT": {
      return value > 0 ? "sema4" : "atmo5";
    }
    default:
      return "atmo5";
  }
};

const IncidentsKpis: React.FC = () => {
  const { t } = useTranslation("map");
  const classes = useStyles();
  const history = useHistory();
  const { pathname } = useLocation();
  const [selectedCard, setSelectedCard] =
    useState<KpiNames>("KPI_OPEN_INCIDENT");

  const [kpiData, setKpiData] = useState<KPIwithColor[]>();

  const path = pathname.toLocaleLowerCase();

  const {
    typeIds,
    mapBound = {
      northEast: { lat: 0, lng: 0 },
      southWest: { lat: 0, lng: 0 },
    },
    refreshTimestamp,
    setIncidentIds,
    setSelectedKpi,
  } = useContext(MapContext);

  const { data, mutate } = useKpis(
    { mapBound, typeIds },
    {
      suspense: kpiData === undefined,
    }
  );

  useEffect(() => {
    mutate();
  }, [refreshTimestamp, mutate]);

  useEffect(() => {
    if (data) {
      setKpiData(
        data.data.map((kpi) => ({
          ...kpi,
          color: getColor(kpi.kpi_name as KpiNames, kpi.count),
        }))
      );

      if (selectedCard) {
        setIncidentIds(
          data.data.find((kpi) => kpi.kpi_name === selectedCard)
            ?.incident_ids as string[]
        );
      }
    }
  }, [data, selectedCard, setIncidentIds]);

  const selectCardCallback = useCallback(
    (name: KpiNames, ids: string[]) => {
      setSelectedCard(name);
      setSelectedKpi(name);
      setIncidentIds(ids);
    },
    [setIncidentIds, setSelectedKpi]
  );

  const multiButtonDisabled = !kpiData?.reduce(
    (disabled, { count }) => disabled || count > 0,
    false
  );

  return (
    <HvGrid container alignItems="flex-start">
      <HvGrid item className={classes.kpiGroup}>
        <HvGrid container>
          {kpiData?.map(({ kpi_name: name, count, color, incident_ids }) => (
            <HvGrid
              item
              xs={3}
              className={clsx(classes.kpi, {
                [classes.selectable]: path === "/incidents" && count > 0,
              })}
              key={name}
            >
              <HvCard
                statusColor={color}
                {...(path === "/incidents" &&
                  count > 0 && {
                    bgcolor: "atmo1",
                    selectable: true,
                    selected: selectedCard === name,
                    role: "button",
                    onClick: () => {
                      if (selectedCard !== name)
                        selectCardCallback(name as KpiNames, incident_ids);
                    },
                    "aria-pressed": selectedCard === name,
                    "aria-selected": selectedCard === name,
                    tabIndex: 0,
                    onKeyDown: ({ keyCode }) => {
                      if (
                        (keyCode === 13 || keyCode === 32) &&
                        selectedCard !== name
                      ) {
                        selectCardCallback(name as KpiNames, incident_ids);
                      }
                    },
                  })}
              >
                <OverflowTooltip
                  data={t(`kpis.${name.toLocaleLowerCase()}.title`) as string}
                  className={classes.kpiTitle}
                />
                <HvTypography className={classes.kpiContent} variant="sTitle">
                  {count >= 0 ? count : "—"}
                </HvTypography>
              </HvCard>
            </HvGrid>
          ))}
        </HvGrid>
      </HvGrid>

      <HvGrid item className={classes.viewSwitchMultiButton}>
        <HvMultiButton vertical>
          <HvButton
            disabled={multiButtonDisabled}
            selected={path === "/map"}
            onClick={() => history.push("/Map")}
          >
            <Map />
          </HvButton>
          <HvButton
            disabled={multiButtonDisabled}
            selected={path === "/incidents"}
            onClick={() => history.push("/incidents")}
          >
            <List />
          </HvButton>
        </HvMultiButton>
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentsKpis;
